/**
 * The header every tool panel in the TOC wears.
 *
 * Written once because eight panels were each hand-rolling their own, and had
 * drifted into a look the side panel cannot carry: a 24px blue heading that
 * repeated the name of the tab already selected right above it, controls with a
 * 16px corner radius on a 44px-tall box, and a red "Close" button sitting in
 * the action row as if closing a panel were as consequential as clearing
 * everything the user drew.
 *
 * Closing belongs HERE, not in the action row. A panel's own chrome is where
 * people look for it, and moving it out leaves the action row to say only what
 * the tool actually does — which is what makes the primary action findable.
 *
 * Panels compose it with the shared glass classes (`gl-btn`, `gl-input`) rather
 * than bespoke Tailwind, so a retheme reaches them like everything else.
 */

import { X } from 'lucide-react';

export default function ToolPanelHeader({
  title,
  description,
  onClose,
}: {
  title: string;
  /** One line on what the tool is for. Omit when the title says it all. */
  description?: string;
  /** Omit and no close control is rendered — no button that does nothing. */
  onClose?: () => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <h2 className="text-[13.5px] font-semibold leading-tight text-slate-800">{title}</h2>
        {description && (
          <p className="mt-0.5 text-[11.5px] leading-snug text-slate-500">{description}</p>
        )}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          title={`Close ${title}`}
          aria-label={`Close ${title}`}
          className="-mr-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * The row a panel's buttons sit in.
 *
 * Wraps rather than gridding into fixed columns. The old
 * `sm:grid-cols-[1.8fr_1fr_1fr]` was keyed to the VIEWPORT width, not the
 * panel's, so on any normal screen it split a ~300px rail into three ~95px
 * columns and broke "Start Drawing" across two lines.
 */
export function ToolActions({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-1.5">{children}</div>;
}
